/**
 * EntryFeeSelector - Unified entry fee selector for all tournaments
 * Supports Golf Coins and Golf Wallet (USD) payment options
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { Coins, Wallet, Lock, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Wallet unlock threshold - users need this many coins OR funds added to use wallet
const WALLET_UNLOCK_COINS = 1000;

const EntryFeeSelector = ({
  entryFee = 0,
  entryFeeType = 'coins', // 'coins' | 'wallet'
  onChangeValue,
  onChangeType,
  playerCount = 1,
  showTotalPot = true,
  disabled = false,
  compact = false
}) => {
  const [userCoins, setUserCoins] = useState(0);
  const [userWalletBalance, setUserWalletBalance] = useState(0);
  const [walletUnlocked, setWalletUnlocked] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch user balances
  useEffect(() => {
    const fetchBalances = async () => {
      try {
        setLoading(true);
        const [coinsRes, walletRes] = await Promise.all([
          axios.get(`${API}/golf-coins/balance`).catch(() => ({ data: { balance: 0 } })),
          axios.get(`${API}/wallet/balance`).catch(() => ({ data: { balance: 0 } }))
        ]);
        
        const coins = coinsRes.data?.balance || 0;
        const wallet = walletRes.data?.balance || 0;
        
        setUserCoins(coins);
        setUserWalletBalance(wallet);
        
        // Wallet is unlocked if user has enough coins OR has added funds
        setWalletUnlocked(coins >= WALLET_UNLOCK_COINS || wallet > 0);
      } catch (error) {
        console.error('Failed to fetch balances:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBalances();
  }, []);

  const coinOptions = [0, 50, 100, 250, 500, 1000];
  const walletOptions = [0, 5, 10, 20, 50, 100];

  const currentOptions = entryFeeType === 'coins' ? coinOptions : walletOptions;
  const currentBalance = entryFeeType === 'coins' ? userCoins : userWalletBalance;
  const hasEnoughBalance = currentBalance >= entryFee;

  const handleTypeChange = (type) => {
    if (type === 'wallet' && !walletUnlocked) {
      return; // Don't allow switching to locked wallet
    }
    onChangeType(type);
    // Reset to first non-zero option for the new type
    const newOptions = type === 'coins' ? coinOptions : walletOptions;
    onChangeValue(newOptions[1] || 0);
  };

  if (compact) {
    return (
      <div className="space-y-3">
        {/* Currency Type Toggle */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => handleTypeChange('coins')}
            disabled={disabled}
            className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
              entryFeeType === 'coins'
                ? 'border-amber-500 bg-amber-500/10'
                : 'border-[var(--ps-border)] bg-[var(--ps-bg-elevated)] hover:border-amber-500/50'
            }`}
            data-testid="entry-type-coins"
          >
            <Coins className={`w-5 h-5 ${entryFeeType === 'coins' ? 'text-amber-500' : 'text-[var(--ps-text-muted)]'}`} />
            <span className={`font-medium ${entryFeeType === 'coins' ? 'text-amber-600' : 'text-[var(--ps-text-secondary)]'}`}>
              Coins
            </span>
          </button>
          
          <button
            type="button"
            onClick={() => handleTypeChange('wallet')}
            disabled={disabled || !walletUnlocked}
            className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
              !walletUnlocked 
                ? 'border-[var(--ps-border)] bg-[var(--ps-bg-elevated)] opacity-50 cursor-not-allowed'
                : entryFeeType === 'wallet'
                  ? 'border-green-500 bg-green-500/10'
                  : 'border-[var(--ps-border)] bg-[var(--ps-bg-elevated)] hover:border-green-500/50'
            }`}
            data-testid="entry-type-wallet"
          >
            {!walletUnlocked ? (
              <Lock className="w-5 h-5 text-[var(--ps-text-muted)]" />
            ) : (
              <Wallet className={`w-5 h-5 ${entryFeeType === 'wallet' ? 'text-green-500' : 'text-[var(--ps-text-muted)]'}`} />
            )}
            <span className={`font-medium ${
              !walletUnlocked 
                ? 'text-[var(--ps-text-muted)]'
                : entryFeeType === 'wallet' ? 'text-green-600' : 'text-[var(--ps-text-secondary)]'
            }`}>
              Wallet
            </span>
          </button>
        </div>

        {/* Wallet Locked Message */}
        {!walletUnlocked && (
          <p className="text-xs text-amber-500 flex items-center gap-1">
            <Lock className="w-3 h-3" />
            Earn {WALLET_UNLOCK_COINS.toLocaleString()} coins or add funds to unlock Golf Wallet
          </p>
        )}

        {/* Entry Fee Options */}
        <div className="grid grid-cols-3 gap-2">
          {currentOptions.map((amount) => (
            <Button
              key={amount}
              type="button"
              variant={entryFee === amount ? 'default' : 'outline'}
              size="sm"
              onClick={() => onChangeValue(amount)}
              disabled={disabled}
              className={
                entryFee === amount
                  ? entryFeeType === 'coins'
                    ? 'bg-amber-600 hover:bg-amber-700 text-white'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                  : ''
              }
            >
              {amount === 0 ? 'Free' : entryFeeType === 'coins' ? amount.toLocaleString() : `$${amount}`}
            </Button>
          ))}
        </div>

        {/* Balance & Pot Info */}
        {entryFee > 0 && (
          <div className="text-xs space-y-1">
            <p className={`flex items-center gap-1 ${hasEnoughBalance ? 'text-[var(--ps-text-muted)]' : 'text-red-500'}`}>
              {!hasEnoughBalance && <AlertCircle className="w-3 h-3" />}
              Your balance: {entryFeeType === 'coins' ? currentBalance.toLocaleString() + ' coins' : '$' + (currentBalance / 100).toFixed(2)}
            </p>
            {showTotalPot && playerCount > 1 && (
              <p className="text-[var(--ps-text-muted)]">
                Total pot: {entryFeeType === 'coins' 
                  ? (entryFee * playerCount).toLocaleString() + ' coins'
                  : '$' + (entryFee * playerCount)
                }
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  // Full size version
  return (
    <div className="space-y-4">
      {/* Header */}
      <Label className="text-[var(--ps-text-secondary)] text-sm">Entry Fee</Label>
      
      {/* Currency Type Cards */}
      <div className="grid grid-cols-2 gap-3">
        {/* Golf Coins Option */}
        <button
          type="button"
          onClick={() => handleTypeChange('coins')}
          disabled={disabled}
          className={`p-4 rounded-xl border-2 transition-all text-left ${
            entryFeeType === 'coins'
              ? 'border-amber-500 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20'
              : 'border-[var(--ps-border)] bg-[var(--ps-bg-elevated)] hover:border-amber-500/50'
          }`}
          data-testid="entry-type-coins-full"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className={`p-2 rounded-lg ${entryFeeType === 'coins' ? 'bg-amber-500/20' : 'bg-[var(--ps-bg-base)]'}`}>
              <Coins className={`w-5 h-5 ${entryFeeType === 'coins' ? 'text-amber-500' : 'text-[var(--ps-text-muted)]'}`} />
            </div>
            <div>
              <p className={`font-semibold ${entryFeeType === 'coins' ? 'text-amber-700 dark:text-amber-400' : 'text-[var(--ps-text-primary)]'}`}>
                Golf Coins
              </p>
              <p className="text-xs text-[var(--ps-text-muted)]">Virtual currency</p>
            </div>
          </div>
          <p className="text-xs text-[var(--ps-text-muted)]">
            Balance: {userCoins.toLocaleString()} coins
          </p>
        </button>

        {/* Golf Wallet Option */}
        <button
          type="button"
          onClick={() => handleTypeChange('wallet')}
          disabled={disabled || !walletUnlocked}
          className={`p-4 rounded-xl border-2 transition-all text-left relative ${
            !walletUnlocked
              ? 'border-[var(--ps-border)] bg-[var(--ps-bg-elevated)] opacity-60 cursor-not-allowed'
              : entryFeeType === 'wallet'
                ? 'border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20'
                : 'border-[var(--ps-border)] bg-[var(--ps-bg-elevated)] hover:border-green-500/50'
          }`}
          data-testid="entry-type-wallet-full"
        >
          {!walletUnlocked && (
            <div className="absolute top-2 right-2">
              <Lock className="w-4 h-4 text-[var(--ps-text-muted)]" />
            </div>
          )}
          <div className="flex items-center gap-2 mb-2">
            <div className={`p-2 rounded-lg ${
              !walletUnlocked 
                ? 'bg-[var(--ps-bg-base)]'
                : entryFeeType === 'wallet' ? 'bg-green-500/20' : 'bg-[var(--ps-bg-base)]'
            }`}>
              <Wallet className={`w-5 h-5 ${
                !walletUnlocked
                  ? 'text-[var(--ps-text-muted)]'
                  : entryFeeType === 'wallet' ? 'text-green-500' : 'text-[var(--ps-text-muted)]'
              }`} />
            </div>
            <div>
              <p className={`font-semibold ${
                !walletUnlocked
                  ? 'text-[var(--ps-text-muted)]'
                  : entryFeeType === 'wallet' ? 'text-green-700 dark:text-green-400' : 'text-[var(--ps-text-primary)]'
              }`}>
                Golf Wallet
              </p>
              <p className="text-xs text-[var(--ps-text-muted)]">Real USD prizes</p>
            </div>
          </div>
          {walletUnlocked ? (
            <p className="text-xs text-[var(--ps-text-muted)]">
              Balance: ${(userWalletBalance / 100).toFixed(2)}
            </p>
          ) : (
            <p className="text-xs text-amber-500">
              Earn {WALLET_UNLOCK_COINS.toLocaleString()} coins to unlock
            </p>
          )}
        </button>
      </div>

      {/* Entry Fee Amount */}
      <div>
        <Label className="text-[var(--ps-text-secondary)] text-sm mb-2 block">
          {entryFeeType === 'coins' ? 'Coins per Player' : 'USD per Player'}
        </Label>
        <div className="grid grid-cols-3 gap-2">
          {currentOptions.map((amount) => (
            <Button
              key={amount}
              type="button"
              variant={entryFee === amount ? 'default' : 'outline'}
              onClick={() => onChangeValue(amount)}
              disabled={disabled}
              className={
                entryFee === amount
                  ? entryFeeType === 'coins'
                    ? 'bg-amber-600 hover:bg-amber-700 text-white'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                  : ''
              }
            >
              {amount === 0 ? 'Free' : entryFeeType === 'coins' ? amount.toLocaleString() : `$${amount}`}
            </Button>
          ))}
        </div>
      </div>

      {/* Summary */}
      {entryFee > 0 && (
        <div className={`p-3 rounded-lg ${
          entryFeeType === 'coins' 
            ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
            : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
        }`}>
          <div className="flex justify-between items-center text-sm">
            <span className="text-[var(--ps-text-secondary)]">Entry per player:</span>
            <span className={`font-semibold ${entryFeeType === 'coins' ? 'text-amber-600' : 'text-green-600'}`}>
              {entryFeeType === 'coins' ? `${entryFee.toLocaleString()} coins` : `$${entryFee}`}
            </span>
          </div>
          {showTotalPot && playerCount > 1 && (
            <div className="flex justify-between items-center text-sm mt-1">
              <span className="text-[var(--ps-text-secondary)]">Total pot ({playerCount} players):</span>
              <span className={`font-bold ${entryFeeType === 'coins' ? 'text-amber-600' : 'text-green-600'}`}>
                {entryFeeType === 'coins' 
                  ? `${(entryFee * playerCount).toLocaleString()} coins`
                  : `$${entryFee * playerCount}`
                }
              </span>
            </div>
          )}
          
          {/* Balance Warning */}
          {!hasEnoughBalance && (
            <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Insufficient balance. You have {entryFeeType === 'coins' 
                ? `${currentBalance.toLocaleString()} coins`
                : `$${(currentBalance / 100).toFixed(2)}`
              }
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default EntryFeeSelector;
